import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getYardsByCompanyId, getReportStats } from "../../pages/Home/api";
import { formatDateToISOString } from "../../utils/utils";

const getDefaultDates = () => {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - 29);
  return {
    startDate: start.toISOString().split("T")[0],
    endDate: today.toISOString().split("T")[0],
  };
};

export const fetchHomeData = createAsyncThunk(
  "home/fetchHomeData",
  async ({ companyId, filters }, { rejectWithValue }) => {
    try {
      const res = await getYardsByCompanyId(companyId);
      if (!res.status || !Array.isArray(res.data))
        throw new Error("Failed to fetch yards");

      const yardOptions = res.data.map((yard) => ({
        label: yard.yard_name,
        value: yard.yard_id,
      }));
      const allYardIds = res.data.map((yard) => yard.yard_id);
      const defaultDates = getDefaultDates();

      const payload = {
        yardIds: filters.yards?.length > 0 ? filters.yards : allYardIds,
        startDate: formatDateToISOString(
          filters.startDate || defaultDates.startDate,
          true
        ),
        endDate: formatDateToISOString(
          filters.endDate || defaultDates.endDate,
          false
        ),
      };

      const reportData = await getReportStats(payload);

      return {
        yardOptions,
        allYardIds,
        reportStats: reportData?.data || [],
        defaultDates,
        lastFetchedFilters: {
          yards: payload.yardIds,
          startDate: payload.startDate,
          endDate: payload.endDate,
        },
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchReportStats = createAsyncThunk(
  "home/fetchReportStats",
  async ({ yardIds, startDate, endDate }, { rejectWithValue }) => {
    try {
      const res = await getReportStats({ yardIds, startDate, endDate });
      return {
        reportStats: res.data,
        lastFetchedFilters: {
          yards: yardIds,
          startDate,
          endDate,
        },
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchYardsForCompany = createAsyncThunk(
  "home/fetchYardsForCompany",
  async ({ companyId }, { rejectWithValue }) => {
    try {
      const res = await getYardsByCompanyId(companyId);
      if (!res.status || !Array.isArray(res.data))
        throw new Error("Failed to fetch yards");

      const yardOptions = res.data.map((yard) => ({
        label: yard.yard_name,
        value: yard.yard_id,
      }));
      const allYardIds = res.data.map((yard) => yard.yard_id);

      return { yardOptions, allYardIds };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const homeSlice = createSlice({
  name: "home",
  initialState: {
    yardOptions: [],
    reportStats: [],
    isLoading: false,
    isYardsLoading: false, 
    error: null,
    lastFetchedFilters: null,
  },
  reducers: {
    resetHomeStats: (state) => {
      state.reportStats = [];
      state.lastFetchedFilters = null;
    },
    resetYards: (state) => {
      state.yardOptions = [];
      state.reportStats = [];
      state.lastFetchedFilters = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHomeData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHomeData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.yardOptions = action.payload.yardOptions;
        state.reportStats = action.payload.reportStats;
        state.lastFetchedFilters = action.payload.lastFetchedFilters;
      })
      .addCase(fetchHomeData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchReportStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReportStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.reportStats = action.payload.reportStats;
        state.lastFetchedFilters = action.payload.lastFetchedFilters;
      })
      .addCase(fetchReportStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchYardsForCompany.pending, (state) => {
        state.isYardsLoading = true;
      })
      .addCase(fetchYardsForCompany.fulfilled, (state, action) => {
        state.isYardsLoading = false;
        state.yardOptions = action.payload.yardOptions;
      })
      .addCase(fetchYardsForCompany.rejected, (state, action) => {
        state.isYardsLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetHomeStats, resetYards } = homeSlice.actions;
export default homeSlice.reducer;